import React, { useState, useEffect, useRef } from "react";
import { Modal } from "reactstrap";
import { ROLE } from "../../../shared/constants/common.js";
import { CREATOR_SECTIONS, USER_SECTIONS } from "../../../shared/constants/message.js";

const Section = ({ icon, title, description, items }) => (
  <div className="flex items-start space-x-3">
    <span className="text-lg">{icon}</span>
    <div>
      <p className="text-white mb-1">{title}</p>
      <p className="text-neutral-400 leading-relaxed mb-2">{description}</p>
      <ul className="text-neutral-400 leading-relaxed space-y-1 ml-4 pl-0">
        {items.map((text, i) => (
          <li key={i} className="flex items-start">
            <span className="mr-2">•</span>
            <span>{text}</span>
          </li>
        ))}
      </ul>
    </div>
  </div>
);

const MessagingExplainedModal = ({
  isOpen,
  toggle,
  userRole
}) => {
  const isCreator = userRole === ROLE.CREATOR_CONTENT || userRole === ROLE.CREATOR_SUB;
  const sections = isCreator ? CREATOR_SECTIONS : USER_SECTIONS;
  const [showScrollIcon, setShowScrollIcon] = useState(true);
  const modalContentRef = useRef(null);
  const handleScrollRef = useRef(null);

  const intro = isCreator
    ? "Once you hit 100 subscribers or 500 followers you can mass message to collect your tips 🎉"
    : null;
  const footer = isCreator ? "Keep those chats rolling 🔥" : "Keep your priority spot 🔥";

  useEffect(() => {
    if (!isOpen) {
      modalContentRef.current = null;
      handleScrollRef.current = null;
      return;
    }

    // Use setTimeout to ensure the modal is rendered in the DOM
    const timer = setTimeout(() => {
      const modalContent = document.querySelector('.cm_msg_exp_modal .modal-content');
      if (!modalContent) return;

      modalContentRef.current = modalContent;

      const handleScroll = () => {
        if (modalContent.scrollTop > 0) {
          setShowScrollIcon(false);
        } else {
          setShowScrollIcon(true);
        }
      };

      handleScrollRef.current = handleScroll;
      modalContent.addEventListener("scroll", handleScroll);
    }, 0);

    return () => {
      clearTimeout(timer);
      if (modalContentRef.current && handleScrollRef.current) {
        modalContentRef.current.removeEventListener("scroll", handleScrollRef.current);
      }
      modalContentRef.current = null;
      handleScrollRef.current = null;
    };
  }, [isOpen]);

  const handleScrollDown = () => {
    const modalContent = modalContentRef.current || document.querySelector('.cm_msg_exp_modal .modal-content');
    if (!modalContent) return;

    modalContent.scrollTo({
      top: modalContent.scrollHeight,
      behavior: "smooth", // Smooth scrolling animation
    });
  };

  return (
    <Modal isOpen={isOpen} backdrop="static" keyboard={false} centered className="cm_msg_exp_modal">
      {showScrollIcon && <span onClick={handleScrollDown} className="cm_scroll_icon">Scroll down <b>↓</b></span>}
      <div className="p-4">
        <div className="d-flex justify-content-center align-items-center mb-3">
          <h5 className="m-0 fw-bold text-white">Messaging Explained</h5>
        </div>

        <span className="cm_close_btn" onClick={toggle} style={{ background: 'transparent' }}>
          <i className="fa-solid fa-xmark"></i>
        </span>

        <div className="space-y-5 text-sm">
          {intro && <p className="text-neutral-400 mb-3">{intro}</p>}
          {sections.map((s, i) => (
            <Section key={i} icon={s.icon} title={s.title} description={s.description} items={s.items} />
          ))}
        </div>

        <p className="text-neutral-300 leading-relaxed pt-2">{footer}</p>
        <div className="mt-8">
          <button className="btn w-full btn_primary text-white mt-3" onClick={toggle}>
            Got it
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default MessagingExplainedModal;
